import React, { useState } from 'react';
import { Card, Button, Form, Spinner } from 'react-bootstrap';
import { Note } from '../../types/types';

interface NoteItemProps {
    note: Note;
    onEdit: (noteId: string, newText: string) => Promise<void>;
    onDelete: (noteId: string) => Promise<void>;
}

const NoteItem: React.FC<NoteItemProps> = ({ note, onEdit, onDelete }) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedText, setEditedText] = useState<string>(note.text);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const handleEdit = async () => {
        if (!editedText.trim()) return;

        setIsSubmitting(true);
        try {
            await onEdit(note.id, editedText);
            setIsEditing(false);
        } catch (error) {
            console.error('Error updating note:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm("Are you sure you want to delete this note?")) {
            setIsDeleting(true);
            try {
                await onDelete(note.id);
            } catch (error) {
                console.error('Error deleting note:', error);
                setIsDeleting(false);
            }
        }
    };

    const formatDateTime = (date: Date) => {
        return new Date(date).toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Card className="mb-3">
            <Card.Body>
                {isEditing ? (
                    <Form>
                        <Form.Group>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={editedText}
                                onChange={(e) => setEditedText(e.target.value)}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end mt-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                className="me-2"
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditedText(note.text);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="success"
                                size="sm"
                                onClick={handleEdit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Save'}
                            </Button>
                        </div>
                    </Form>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="note-content">
                                <p className="mb-2">{note.text}</p>
                                <small className="text-muted">
                                    Added by {note.createdBy} on {formatDateTime(note.createdAt)}
                                    {note.isEdited && note.editedAt && (
                                        <> � Edited on {formatDateTime(note.editedAt)}</>
                                    )}
                                </small>
                            </div>
                            <div className="note-actions">
                                <Button
                                    variant="link"
                                    className="p-0 me-2"
                                    onClick={() => setIsEditing(true)}
                                >
                                    <i className="bi bi-pencil"></i>
                                </Button>
                                <Button
                                    variant="link"
                                    className="p-0 text-danger"
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ?
                                        <Spinner animation="border" size="sm" /> :
                                        <i className="bi bi-trash"></i>
                                    }
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default NoteItem;